'use client';

import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { cloneElement, forwardRef, useCallback, useMemo, useRef } from 'react';
import type { AnimationControls } from 'framer-motion';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import type { AddDollarPrefix } from '@frameio/utils';
import type { AvatarProps } from '@frameio/vapor';
import {
  Button,
  MenuPrimaryAction,
  squareRadius,
  theme,
  useTimeDeferredMount,
  whenFocused,
  whenHovered,
  whenPressed,
} from '@frameio/vapor';
import { IconEditFill } from '@frameio/vapor-icons';

export type AvatarButtonProps = {
  adornment?: ReactNode;
  avatar: ReactElement<any>;
  avatarSize: AvatarProps['size'];
  controls?: AnimationControls;
  delayMount?: number;
  disableOverlay?: boolean;
  label: string;
  nextAvatarSrc: string;
  showOverlay?: boolean;
  isMenuItem?: boolean;
} & ComponentProps<typeof Button> &
  Pick<AvatarProps, 'shape'>;

type StyledShapeProps = AddDollarPrefix<
  Pick<AvatarButtonProps, 'shape' | 'avatarSize'>
>;

const Overlay = styled.div<{ $isOverlayShowing?: boolean } & StyledShapeProps>`
  align-items: center;
  background-color: ${theme.colors.overlay.medium};
  border-radius: ${(p) =>
    p.$shape === 'circle' ? '50%' : `${squareRadius(p.$avatarSize)}px`};
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  opacity: ${(p) => (p.$isOverlayShowing ? 1 : 0)};
  /* prettier-ignore */
  position: absolute;
  top: 0;
  transition: opacity ${theme.transitions.durations[200]}
    ${theme.transitions.eases.primary};
  width: 100%;
`;

const buttonStyles = css<StyledShapeProps>`
  border-radius: ${(p) =>
    p.$shape === 'circle' ? '50%' : `${squareRadius(p.$avatarSize)}px`};
  height: ${(p) => p.$avatarSize}px;
  outline: none;
  width: ${(p) => p.$avatarSize}px;

  ${whenPressed},
  ${whenFocused},
  ${whenHovered} {
    ${Overlay} {
      opacity: 1;
    }
  }
`;
export const StyledButton = styled(Button)<StyledShapeProps>`
  ${buttonStyles}
`;

export const StyledMenuPrimaryAction = styled(MenuPrimaryAction).attrs({
  shouldCloseMenuOnSelect: false,
})<StyledShapeProps>`
  ${buttonStyles}
  overflow: visible;
`;

export const AvatarButton = forwardRef(
  (
    {
      adornment = null,
      avatar,
      avatarSize,
      controls,
      delayMount = 200,
      disableOverlay,
      label,
      nextAvatarSrc,
      shape,
      showOverlay,
      isMenuItem = false,
      ...rest
    }: AvatarButtonProps,
    forwardedRef
  ) => {
    const allowKeyboardToOpenEditor = useRef<boolean>(false);
    const shouldMount = useTimeDeferredMount(delayMount);
    const ButtonComponent = useMemo(
      () => (isMenuItem ? StyledMenuPrimaryAction : StyledButton),
      [isMenuItem]
    ) as typeof StyledButton;

    // prevents keyboard events from opening AvatarEditor when opening the AccountSwitcher in primary-nav via keyboard
    const handleKeyPress = useCallback((e) => {
      if (!allowKeyboardToOpenEditor.current) {
        e.preventDefault();
      }
    }, []);
    const handleKeyDown = useCallback(() => {
      allowKeyboardToOpenEditor.current = true;
    }, []);
    const handleKeyUp = useCallback(() => {
      allowKeyboardToOpenEditor.current = false;
    }, []);

    return (
      <ButtonComponent
        ref={forwardedRef}
        $avatarSize={avatarSize}
        $shape={shape}
        aria-label={label}
        onKeyDown={handleKeyDown}
        onKeyPress={handleKeyPress}
        onKeyUp={handleKeyUp}
        type="button"
        variant="link"
        {...rest}
      >
        {shouldMount && (
          <>
            {adornment}
            {avatar}
            <motion.div
              animate={controls}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: 0,
              }}
            >
              {cloneElement(avatar, { src: nextAvatarSrc })}
            </motion.div>
            {!disableOverlay ? (
              <Overlay
                $avatarSize={avatarSize}
                $isOverlayShowing={showOverlay}
                $shape={shape}
              >
                <IconEditFill
                  color={theme.colors.components.icon.button.primary}
                />
              </Overlay>
            ) : null}
          </>
        )}
      </ButtonComponent>
    );
  }
);
